<template>
  <div class="pa-2 fleet-charging-panel">
    <v-card flat class="mb-4">
      <v-card-title>
        <span class="headline">Fleet Charging</span>
        <v-spacer />
        <v-btn v-if="canManageCharging" color="primary" @click="openAssignVehicleDialog">
          Assign vehicle
        </v-btn>
      </v-card-title>
      <v-card-text v-if="!canManageCharging">
        <v-alert v-if="!canManageCharging" type="info" :value="true" class="mb-3">
          You have read-only access to fleet charging assignments.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-tabs v-model="activeFleetSubTab" class="mb-3" @change="onFleetSubTabChanged">
      <v-tabs-slider color="primary" />
      <v-tab ripple>Charging Sessions</v-tab>
      <v-tab ripple>Current Assignments</v-tab>
    </v-tabs>

    <div v-show="activeFleetSubTab === 0">
      <v-card flat>
        <v-card-text>
          <v-layout row wrap align-center>
            <v-flex xs12 sm3 pa-2>
              <v-text-field v-model="filters.chargePointId" label="Charge Point ID" clearable hide-details @keyup.enter.native="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm3 pa-2>
              <v-text-field v-model="filters.idTag" label="idTag" clearable hide-details @keyup.enter.native="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pa-2>
              <st-date-field id="fleet-charging-date-from" v-model="filters.dateFrom" label="From" hide-details to-date-id="fleet-charging-date-to" @change="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pl-4 pa-2>
              <st-date-field id="fleet-charging-date-to" v-model="filters.dateTo" label="To" hide-details from-date-id="fleet-charging-date-from" @change="refreshQueue" />
            </v-flex>
            <v-flex xs12 sm2 pa-2 class="text-xs-right">
              <v-btn fab small :loading="isLoading" title="Refresh sessions" @click="refreshQueue">
                <v-icon>fal fa-filter</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-data-table
          :headers="headers"
          :items="sessions"
          :loading="isLoading"
          :pagination.sync="sessionsPagination"
          :custom-sort="passthroughSort"
          :rows-per-page-items="[10, 25, 50]"
        >
          <template v-slot:items="{ item }">
            <tr>
              <td>{{ item.charge_point_id || '-' }}</td>
              <td>{{ item.id_tag || '-' }}</td>
              <td>{{ item.status || '-' }}</td>
              <td class="fleet-charging-panel__progress-cell">
                <div v-if="isSessionInProgress(item)">
                  <v-progress-linear
                    :value="sessionProgressValue(item)"
                    :indeterminate="sessionProgressIndeterminate(item)"
                    height="8"
                    color="primary"
                    class="my-1"
                  />
                  <div class="caption">{{ sessionProgressLabel(item, false) }}</div>
                </div>
                <div v-else>{{ sessionProgressLabel(item, false) }}</div>
              </td>
              <td>{{ item.license_plate_number || '-' }}</td>
              <td>{{ formatDateTime(item.start_time) }}</td>
              <td class="text-xs-right">
                <v-btn v-if="canManageCharging" small @click="openSessionDialog(item)">
                  {{ item.transportation_profile_id ? 'Manage' : 'Assign Vehicle' }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <div v-show="activeFleetSubTab === 1">
      <v-card flat class="mb-4">
        <v-card-title>
          <span class="title">Current Assignments</span>
          <v-spacer />
          <v-btn small flat :loading="activeAssignmentsLoading" @click="refreshActiveAssignments">Refresh</v-btn>
        </v-card-title>
        <v-data-table
          :headers="activeAssignmentHeaders"
          :items="activeAssignments"
          :loading="activeAssignmentsLoading"
          :rows-per-page-items="[10, 25, 50]"
        >
          <template v-slot:items="{ item }">
            <tr>
              <td>{{ item.charge_point_id || '-' }}</td>
              <td>{{ item.connector_id ? 'Gun ' + item.connector_id : '-' }}</td>
              <td>{{ item.location_label || '-' }}</td>
              <td>{{ item.license_plate_number || '-' }}</td>
              <td>{{ item.transaction_status || item.status || '-' }}</td>
              <td>{{ formatDateTime(item.date_created) }}</td>
              <td class="text-xs-right">
                <v-btn
                  v-if="canManageCharging"
                  small
                  color="warning"
                  :loading="actionInProgress"
                  @click="releaseAssignment(item)"
                >
                  Release
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="assignDialogVisible" max-width="1100" @input="onAssignDialogInput">
      <v-card>
        <v-card-title>
          <span class="headline">Assign Vehicle to Charger</span>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md5 pa-2>
              <transportation-profile-autocomplete
                v-model="selectedProfile"
                label="Vehicle"
                placeholder="Search by plate, shortcode, or driver"
                :show-owner-account="true"
                :show-driver="true"
                :disabled="!canManageCharging || actionInProgress"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex xs12 md7 pa-2>
              <st-map
                v-if="stMapApis && assignDialogVisible"
                disable-search
                disable-map-hash
                container-id="fleetChargingPortalMap"
                height="360px"
                :mapbox-access-token="mapboxAccessToken"
                :maptiler-key="maptilerKey"
                :api="stMapApis"
                :on-map-loaded="onMapLoaded"
              />
            </v-flex>
            <v-flex xs12 md5 pa-2>
              <v-text-field
                v-model="chargerSearch"
                label="Charger name or location"
                append-icon="fal fa-search"
                clearable
                :loading="searchInProgress"
                :disabled="searchInProgress"
                @keyup.enter.native="searchChargers"
                @click:append="searchChargers"
              />
              <v-list two-line class="fleet-charging-panel__charger-list">
                <v-list-tile
                  v-for="charger in chargers"
                  :key="charger.id"
                  :class="{ 'primary--text': selectedCharger && selectedCharger.id === charger.id }"
                  @click="selectCharger(charger)"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{ charger.chargePointId }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ charger.locationLabel || 'No location label' }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-chip small :color="isChargerInUse(charger) ? 'warning' : 'success'" text-color="white">
                      {{ isChargerInUse(charger) ? 'In use' : 'Free' }}
                    </v-chip>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
              <v-select
                v-if="selectedCharger"
                v-model="selectedConnectorId"
                :items="connectorOptionsForCharger(selectedCharger)"
                item-text="text"
                item-value="connectorId"
                item-disabled="disabled"
                label="Gun"
                :disabled="actionInProgress"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn flat color="warning" @click="assignDialogVisible = false">Cancel</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="actionInProgress"
            :disabled="!canAssignSelectedCharger"
            @click="assignSelectedCharger"
          >
            Assign Vehicle to Charger
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogVisible" max-width="920">
      <v-card>
        <v-card-title><span class="headline">Session Assignment</span></v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md6>
              <div><strong>Charge Point:</strong> {{ selectedSession && selectedSession.charge_point_id ? selectedSession.charge_point_id : '-' }}</div>
              <div><strong>Transaction:</strong> {{ selectedSession && selectedSession.ocpp_transaction_id ? selectedSession.ocpp_transaction_id : '-' }}</div>
              <div><strong>idTag:</strong> {{ selectedSession && selectedSession.id_tag ? selectedSession.id_tag : '-' }}</div>
              <div><strong>Status:</strong> {{ selectedSession && selectedSession.status ? selectedSession.status : '-' }}</div>
            </v-flex>
            <v-flex xs12 md6>
              <div><strong>Verification:</strong> {{ selectedSession && selectedSession.verification_status ? selectedSession.verification_status : '-' }}</div>
              <div><strong>Start:</strong> {{ selectedSession ? formatDateTime(selectedSession.start_time) : '-' }}</div>
              <div><strong>Stop:</strong> {{ selectedSession ? formatDateTime(selectedSession.stop_time) : '-' }}</div>
              <div><strong>Energy:</strong> {{ selectedSession ? formatKwh(selectedSession.energy_delivered_kwh) : '-' }}</div>
            </v-flex>
          </v-layout>
          <div v-if="selectedSessionIsActive" class="mt-3">
            <v-progress-linear
              :value="sessionProgressValue(selectedSession)"
              :indeterminate="sessionProgressIndeterminate(selectedSession)"
              height="12"
              color="primary"
              class="my-1"
            />
            <div><strong>Charging progress:</strong> {{ sessionProgressLabel(selectedSession, true) }}</div>
            <div><strong>State of charge:</strong> {{ formatSoc(sessionStateOfCharge(selectedSession)) }}</div>
          </div>
          <v-divider class="my-4" />
          <v-alert v-if="selectedSession && !selectedSessionIsActive" type="info" :value="true" class="mb-3">
            This charging session is complete. You can still assign a vehicle for audit history, but provisioning actions require an active charging session.
          </v-alert>
          <transportation-profile-autocomplete
            v-model="assignment.transportationProfile"
            label="Vehicle to assign"
            placeholder="Search by plate, shortcode, or driver"
            :show-owner-account="true"
            :show-driver="true"
            :disabled="!canManageCharging"
          />
          <v-layout row wrap>
            <v-flex xs12 sm6 pa-1>
              <v-select v-model="assignment.vehicleClass" :items="vehicleClasses" item-text="text" item-value="value" label="Vehicle class for idTag" />
            </v-flex>
            <v-flex xs12 sm6 pa-1 class="d-flex align-center">
              <v-checkbox v-model="assignment.applyToIdTag" label="Apply assignment to idTag for future sessions" hide-details />
            </v-flex>
            <v-flex xs12>
              <v-textarea v-model="assignment.notes" rows="2" auto-grow label="Assignment notes (optional)" />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="primary" :loading="actionInProgress" :disabled="!canManageCharging || actionInProgress" @click="onAssignSession">
                Assign Vehicle
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="warning" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onStartProvisioning">
                Start Provisioning
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="success" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onFinalizeProvisioning">
                Finalize Provisioning
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mt-1">
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="secondary" :loading="actionInProgress" :disabled="!canRunSelectedSessionIdTagAction" @click="onReProvision">
                Re-Provision
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="error" :loading="actionInProgress" :disabled="!canStopSelectedSession" @click="onStopSelectedSession">
                Stop Charging
              </v-btn>
            </v-flex>
            <v-flex xs12 sm4 pa-1>
              <v-btn block color="error" outline :loading="actionInProgress" :disabled="!canCloseSelectedStaleSession" @click="onCloseSelectedStaleSession">
                Close Stale Session
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat color="warning" @click="dialogVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatDate, formatTime } from '@smarttransit/common'
import { addAlert, isAuthorized } from '@/utilities/helpers'
import { genericApiRequests } from '@/utilities/axios-factory'
import { findOcppChargers } from '@/services/ocpp-charger-service'
import {
  getChargingAssignmentQueue,
  assignChargingSession,
  startProvisioning,
  finalizeProvisioning,
  reProvision,
  assignVehicleToCharger,
  releaseVehicleFromCharger,
  stopChargingTransaction,
  closeStaleChargingSession,
  getActiveChargerAssignments
} from '@/services/ocpp-assignment-service'

const MAP_SOURCE_ID = 'fleetChargingAssignmentSource'
const MAP_LAYER_ID = 'fleetChargingAssignmentLayer'
const MAP_HIT_LAYER_ID = 'fleetChargingAssignmentHitLayer'
const MAP_IMAGE_ID = 'ev-charger-marker'
const MAP_MARKER_ICON_SIZE = 0.28
const SESSION_STATUS_POLL_INTERVAL_MS = 15000
const FLEET_SUB_TAB_SESSIONS = 0
const FLEET_SUB_TAB_ASSIGNMENTS = 1

function mapPopupHtml (charger) {
  return `
    <h4>${charger.chargePointId || '-'}</h4>
    <div>Status: ${charger.status || '-'}</div>
    <div>Model: ${charger.model || '-'}</div>
    <div><small>${charger.locationLabel || '-'}</small></div>
  `
}

export default {
  name: 'fleet-charging-panel',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      activeFleetSubTab: 0,
      assignDialogVisible: false,
      selectedProfile: null,
      selectedCharger: null,
      selectedConnectorId: null,
      chargerSearch: '',
      chargers: [],
      stMapApis: null,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      panelIsActive: false,
      searchInProgress: false,
      activeAssignmentsLoading: false,
      activeAssignments: [],
      sessions: [],
      sessionPollHandle: null,
      sessionPollInProgress: false,
      mapboxInstanceWrapper: null,
      mapMarkerEventsRegistered: false,
      mapPopup: null,
      isLoading: false,
      actionInProgress: false,
      dialogVisible: false,
      selectedSession: null,
      filters: {
        chargePointId: '',
        idTag: '',
        dateFrom: '',
        dateTo: ''
      },
      sessionsPagination: {
        sortBy: null,
        descending: false,
        page: 1,
        rowsPerPage: 10
      },
      assignment: {
        transportationProfile: null,
        vehicleClass: 'fleet_bus',
        applyToIdTag: true,
        notes: ''
      },
      vehicleClasses: [
        { text: 'Fleet bus', value: 'fleet_bus' },
        { text: 'Private vehicle', value: 'private_vehicle' }
      ],
      headers: [
        { text: 'Charge Point', value: 'charge_point_id', sortable: true },
        { text: 'idTag', value: 'id_tag', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Progress', value: 'progress', sortable: false },
        { text: 'Vehicle', value: 'license_plate_number', sortable: true },
        { text: 'Start', value: 'start_time', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],
      activeAssignmentHeaders: [
        { text: 'Charge Point', value: 'charge_point_id', sortable: true },
        { text: 'Gun', value: 'connector_id', sortable: true },
        { text: 'Location', value: 'location_label', sortable: true },
        { text: 'Vehicle', value: 'license_plate_number', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Assigned', value: 'date_created', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    canManageCharging () {
      return isAuthorized(this.signedInUser, 'agentdriver')
    },
    canAssignSelectedCharger () {
      return this.canManageCharging && this.selectedProfile && this.selectedProfile.id && this.selectedCharger && this.selectedConnectorId && !this.isSelectedConnectorInUse() && !this.actionInProgress
    },
    selectedSessionIsActive () {
      return this.isSessionInProgress(this.selectedSession)
    },
    canRunSelectedSessionIdTagAction () {
      return this.canManageCharging && !this.actionInProgress && this.selectedSessionIsActive && !!(this.selectedSession && this.selectedSession.id_tag)
    },
    canStopSelectedSession () {
      return this.canManageCharging && !this.actionInProgress && this.selectedSessionIsActive && !!(this.selectedSession && this.selectedSession.charge_point_id && this.selectedSession.ocpp_transaction_id)
    },
    canCloseSelectedStaleSession () {
      return isAuthorized(this.signedInUser, 'admin') && !this.actionInProgress && this.selectedSessionIsActive && !!(this.selectedSession && this.selectedSession.ocpp_transaction_id)
    },
    sessionsOrder () {
      const { sortBy, descending } = this.sessionsPagination
      if (!sortBy || descending === null) {
        return null
      }
      return `${sortBy} ${descending ? 'DESC' : 'ASC'}`
    }
  },
  watch: {
    sessionsOrder () {
      this.refreshQueue()
    },
    sessionsPagination: {
      handler () {
        this.scheduleSessionPoll()
      },
      deep: true
    }
  },
  mounted () {
    this.stMapApis = {
      genericApiRequests,
      addAlert
    }
    this.searchChargers()
    this.refreshActiveAssignments()
    this.refreshQueue()
  },
  methods: {
    formatDateTime (value) {
      if (!value) {
        return '-'
      }
      return `${formatTime(value)}, ${formatDate(value)}`
    },
    formatKwh (value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return `${value} kWh`
    },
    formatSoc (value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return `${Number(value).toFixed(0)}%`
    },
    isSessionInProgress (session) {
      const status = session && session.status ? String(session.status).toLowerCase() : ''
      return status === 'inprogress' && !(session && session.stop_time)
    },
    sessionStateOfCharge (session) {
      if (!session || session.state_of_charge_pct === null || session.state_of_charge_pct === undefined || session.state_of_charge_pct === '') {
        return null
      }
      const value = Number(session.state_of_charge_pct)
      if (Number.isNaN(value)) {
        return null
      }
      if (value < 0) {
        return 0
      }
      if (value > 100) {
        return 100
      }
      return value
    },
    sessionProgressValue (session) {
      const stateOfCharge = this.sessionStateOfCharge(session)
      return stateOfCharge === null ? 0 : stateOfCharge
    },
    sessionProgressIndeterminate (session) {
      return this.isSessionInProgress(session) && this.sessionStateOfCharge(session) === null
    },
    sessionProgressLabel (session, includePercentage) {
      const energyLabel = this.formatKwh(session && session.energy_delivered_kwh)
      const stateOfCharge = this.sessionStateOfCharge(session)
      if (includePercentage && stateOfCharge !== null) {
        return energyLabel + ' (' + this.formatSoc(stateOfCharge) + ')'
      }
      return energyLabel
    },
    visibleSessionRows () {
      const sessions = this.sessions || []
      const rowsPerPage = Number(this.sessionsPagination && this.sessionsPagination.rowsPerPage)
      if (!rowsPerPage || rowsPerPage < 1) {
        return sessions
      }
      const page = Number(this.sessionsPagination && this.sessionsPagination.page) || 1
      const start = (page - 1) * rowsPerPage
      return sessions.slice(start, start + rowsPerPage)
    },
    visibleInProgressTransactionIds () {
      const transactionIds = []
      this.visibleSessionRows().forEach((session) => {
        if (!this.isSessionInProgress(session) || !session.ocpp_transaction_id) {
          return
        }
        const transactionId = Number(session.ocpp_transaction_id)
        if (transactionId && transactionIds.indexOf(transactionId) === -1) {
          transactionIds.push(transactionId)
        }
      })
      return transactionIds
    },
    stopSessionPoll () {
      clearTimeout(this.sessionPollHandle)
      this.sessionPollHandle = null
    },
    scheduleSessionPoll () {
      this.stopSessionPoll()
      if (!this.panelIsActive || Number(this.activeFleetSubTab) !== FLEET_SUB_TAB_SESSIONS || this.isLoading || this.sessionPollInProgress) {
        return
      }
      if (!this.visibleInProgressTransactionIds().length) {
        return
      }
      this.sessionPollHandle = setTimeout(() => {
        this.refreshVisibleInProgressSessions()
      }, SESSION_STATUS_POLL_INTERVAL_MS)
    },
    syncSelectedSessionFromSessions () {
      if (!this.selectedSession || !this.selectedSession.ocpp_transaction_id) {
        return
      }
      const selectedTransactionId = String(this.selectedSession.ocpp_transaction_id)
      const updatedSession = (this.sessions || []).find((session) => String(session.ocpp_transaction_id) === selectedTransactionId)
      if (updatedSession) {
        this.selectedSession = Object.assign({}, this.selectedSession, updatedSession)
      }
    },
    mergeSessionUpdates (updates) {
      const sessionsByTransactionId = {}
      const updateList = updates || []
      updateList.forEach((session) => {
        if (session && session.ocpp_transaction_id) {
          sessionsByTransactionId[String(session.ocpp_transaction_id)] = session
        }
      })
      if (!Object.keys(sessionsByTransactionId).length) {
        return
      }
      this.sessions = (this.sessions || []).map((session) => {
        const update = session && session.ocpp_transaction_id ? sessionsByTransactionId[String(session.ocpp_transaction_id)] : null
        return update ? Object.assign({}, session, update) : session
      })
      this.syncSelectedSessionFromSessions()
    },
    async refreshVisibleInProgressSessions () {
      this.stopSessionPoll()
      const transactionIds = this.visibleInProgressTransactionIds()
      if (!this.panelIsActive || Number(this.activeFleetSubTab) !== FLEET_SUB_TAB_SESSIONS || !transactionIds.length) {
        return
      }
      this.sessionPollInProgress = true
      try {
        const updates = await getChargingAssignmentQueue({
          transactionIds,
          limit: transactionIds.length
        })
        this.mergeSessionUpdates(updates)
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.sessionPollInProgress = false
        this.scheduleSessionPoll()
      }
    },
    connectorOptionsForCharger (charger) {
      const hasConnectors = charger && Array.isArray(charger.connectors) && charger.connectors.length
      const connectors = hasConnectors
        ? charger.connectors
        : [{
          connectorId: 1,
          availability: {
            inUse: this.isChargerInUse(charger)
          }
        }]
      return connectors.map((connector) => {
        const connectorId = Number(connector.connectorId)
        const inUse = this.isConnectorInUse(connector)
        return {
          connectorId,
          text: 'Gun ' + connectorId + (inUse ? ' - In use' : ' - Free'),
          disabled: inUse
        }
      })
    },
    isConnectorInUse (connector) {
      return !!(connector && connector.availability && connector.availability.inUse)
    },
    isSelectedConnectorInUse () {
      const connectorId = Number(this.selectedConnectorId)
      const selectedOption = this.connectorOptionsForCharger(this.selectedCharger).find((option) => option.connectorId === connectorId)
      return !selectedOption || selectedOption.disabled
    },
    firstAssignableConnectorId (charger) {
      const options = this.connectorOptionsForCharger(charger)
      const freeOption = options.find((option) => !option.disabled)
      const selectedOption = freeOption || options[0]
      return selectedOption ? selectedOption.connectorId : null
    },
    isChargerInUse (charger) {
      return !!(charger && charger.availability && charger.availability.inUse)
    },
    selectCharger (charger) {
      this.selectedCharger = charger
      this.selectedConnectorId = this.firstAssignableConnectorId(charger)
    },
    focusPanel () {
      this.panelIsActive = true
      this.refreshActiveFleetSubTab()
      this.focusMap()
    },
    blurPanel () {
      this.panelIsActive = false
      this.stopSessionPoll()
    },
    refreshActiveFleetSubTab () {
      if (Number(this.activeFleetSubTab) === FLEET_SUB_TAB_SESSIONS) {
        return this.refreshQueue()
      }
      this.stopSessionPoll()
      if (Number(this.activeFleetSubTab) === FLEET_SUB_TAB_ASSIGNMENTS) {
        return this.refreshActiveAssignments()
      }
      return null
    },
    onFleetSubTabChanged (value) {
      this.activeFleetSubTab = Number(value)
      if (Number(this.activeFleetSubTab) !== FLEET_SUB_TAB_SESSIONS) {
        this.stopSessionPoll()
      }
      return this.refreshActiveFleetSubTab()
    },
    openAssignVehicleDialog () {
      this.assignDialogVisible = true
      this.searchChargers()
      this.$nextTick(() => {
        this.focusMap()
      })
    },
    onAssignDialogInput (value) {
      if (value) {
        this.focusMap()
        return
      }
      this.closeMapPopup()
    },
    onMapLoaded (mapboxInstance) {
      this.mapboxInstanceWrapper = Object.freeze({ mapboxInstance })
      this.mapMarkerEventsRegistered = false
      this.closeMapPopup()
      return this.focusMap()
    },
    getMapboxInstance () {
      return this.mapboxInstanceWrapper ? this.mapboxInstanceWrapper.mapboxInstance : null
    },
    focusMap () {
      this.$nextTick(() => {
        setTimeout(() => {
          const mapboxInstance = this.getMapboxInstance()
          window.dispatchEvent(new Event('resize'))
          if (mapboxInstance && mapboxInstance.currentMap && typeof mapboxInstance.currentMap.resize === 'function') {
            mapboxInstance.currentMap.resize()
          }
          this.renderMapMarkers()
        }, 100)
      })
    },
    closeMapPopup () {
      if (this.mapPopup) {
        this.mapPopup.remove()
        this.mapPopup = null
      }
    },
    showMapPopup (feature, options) {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap || !feature || !feature.properties || !feature.geometry) {
        return
      }
      options = options || {}

      this.closeMapPopup()
      this.mapPopup = mapboxInstance.createPopup({
        className: 'st-map--popup',
        closeButton: !!options.closeButton,
        closeOnClick: !!options.closeOnClick,
        offset: 12
      })
      this.mapPopup.on('close', () => {
        this.mapPopup = null
      })
      this.mapPopup
        .setLngLat(feature.geometry.coordinates.slice())
        .setHTML(mapPopupHtml(feature.properties))
        .addTo(mapboxInstance.currentMap)
    },
    cloneMapFeature (feature) {
      if (!feature) {
        return null
      }
      return JSON.parse(JSON.stringify({
        geometry: {
          type: feature.geometry && feature.geometry.type,
          coordinates: feature.geometry && feature.geometry.coordinates ? feature.geometry.coordinates.slice() : []
        },
        properties: Object.assign({}, feature.properties || {})
      }))
    },
    async renderMapMarkers () {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap) {
        return
      }
      const currentMap = mapboxInstance.currentMap
      const features = (this.chargers || [])
        .filter((charger) => typeof charger.longitude === 'number' && typeof charger.latitude === 'number')
        .map((charger) => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [charger.longitude, charger.latitude]
            },
            properties: {
              id: charger.id,
              chargePointId: charger.chargePointId || '',
              status: charger.status || '',
              vendor: charger.vendor || '',
              model: charger.model || '',
              locationLabel: charger.locationLabel || '',
              inUse: this.isChargerInUse(charger),
              longitude: charger.longitude,
              latitude: charger.latitude,
              lng: charger.longitude,
              lat: charger.latitude
            }
          }
        })

      const sourceData = {
        type: 'FeatureCollection',
        features
      }

      if (currentMap.getSource(MAP_SOURCE_ID)) {
        currentMap.getSource(MAP_SOURCE_ID).setData(sourceData)
      } else {
        currentMap.addSource(MAP_SOURCE_ID, {
          type: 'geojson',
          data: sourceData
        })
      }

      if (!currentMap.hasImage(MAP_IMAGE_ID)) {
        await mapboxInstance.addLayerImage(`${location.origin}/images/ev-charger-marker.png`, MAP_IMAGE_ID)
      }

      if (!currentMap.getLayer(MAP_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_LAYER_ID,
          type: 'symbol',
          source: MAP_SOURCE_ID,
          layout: {
            'icon-image': MAP_IMAGE_ID,
            'icon-size': MAP_MARKER_ICON_SIZE,
            'icon-anchor': 'center',
            'icon-allow-overlap': true
          }
        })
      } else {
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-size', MAP_MARKER_ICON_SIZE)
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-anchor', 'center')
      }

      if (!currentMap.getLayer(MAP_HIT_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_HIT_LAYER_ID,
          type: 'circle',
          source: MAP_SOURCE_ID,
          paint: {
            'circle-radius': 22,
            'circle-color': '#204da2',
            'circle-opacity': 0.01
          }
        })
      }

      this.registerMapMarkerEvents()
    },
    registerMapMarkerEvents () {
      const mapboxInstance = this.getMapboxInstance()
      if (this.mapMarkerEventsRegistered || !mapboxInstance || !mapboxInstance.currentMap) {
        return
      }

      const currentMap = mapboxInstance.currentMap

      currentMap.on('click', MAP_HIT_LAYER_ID, (event) => {
        const feature = this.cloneMapFeature(event && event.features && event.features.length ? event.features[0] : null)
        if (!feature || !feature.properties || !feature.properties.id) {
          return
        }
        const charger = this.chargers.find((item) => item.id === feature.properties.id)
        if (charger) {
          this.selectCharger(charger)
        }
      })

      currentMap.on('mouseenter', MAP_HIT_LAYER_ID, (event) => {
        const feature = this.cloneMapFeature(event && event.features && event.features.length ? event.features[0] : null)
        if (!feature || !feature.properties || !feature.properties.id) {
          return
        }

        currentMap.getCanvas().style.cursor = 'pointer'
        this.showMapPopup(feature, { closeButton: false, closeOnClick: false })
      })

      currentMap.on('mouseleave', MAP_HIT_LAYER_ID, () => {
        currentMap.getCanvas().style.cursor = ''
        this.closeMapPopup()
      })

      this.mapMarkerEventsRegistered = true
    },
    async searchChargers () {
      this.searchInProgress = true
      try {
        this.chargers = await findOcppChargers({
          limit: 50,
          offset: 0,
          order: 'dateUpdated DESC',
          search: this.chargerSearch || undefined
        })
        if (this.selectedCharger) {
          const updatedSelected = this.chargers.find((charger) => charger.id === this.selectedCharger.id)
          this.selectedCharger = updatedSelected || null
          if (this.selectedCharger && this.selectedConnectorId && this.connectorOptionsForCharger(this.selectedCharger).find((option) => option.connectorId === Number(this.selectedConnectorId))) {
            this.selectedConnectorId = Number(this.selectedConnectorId)
          } else {
            this.selectedConnectorId = this.firstAssignableConnectorId(this.selectedCharger)
          }
        }
        this.$nextTick(() => {
          this.focusMap()
        })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.searchInProgress = false
      }
    },
    async assignSelectedCharger () {
      if (!this.canAssignSelectedCharger) {
        return
      }
      this.actionInProgress = true
      try {
        const result = await assignVehicleToCharger({
          chargePointId: this.selectedCharger.chargePointId,
          connectorId: this.selectedConnectorId,
          transportationProfileId: this.selectedProfile.id
        })
        if (result && result.remoteStart) {
          addAlert({ message: 'Vehicle assigned and remote start request sent', type: 'success' })
        } else {
          addAlert({ message: 'Vehicle assigned to charger', type: 'success' })
        }
        this.assignDialogVisible = false
        await Promise.all([this.searchChargers(), this.refreshActiveAssignments(), this.refreshQueue()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async refreshActiveAssignments () {
      this.activeAssignmentsLoading = true
      try {
        this.activeAssignments = await getActiveChargerAssignments()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.activeAssignmentsLoading = false
      }
    },
    async releaseAssignment (assignment) {
      if (!assignment || !assignment.charge_point_id) {
        return
      }
      const connectorLabel = assignment.connector_id ? ' gun ' + assignment.connector_id : ''
      const chargerLabel = assignment.charge_point_id + connectorLabel
      const vehicleLabel = assignment.license_plate_number ? ' for ' + assignment.license_plate_number : ''
      if (!confirm('Confirm releasing the charger assignment' + vehicleLabel + ' from ' + chargerLabel + '?')) {
        return
      }
      this.actionInProgress = true
      try {
        const result = await releaseVehicleFromCharger({
          chargePointId: assignment.charge_point_id,
          connectorId: assignment.connector_id
        })
        if (result && result.remoteStop) {
          addAlert({ message: 'Assignment released and remote stop request sent', type: 'success' })
        } else {
          addAlert({ message: 'Assignment released', type: 'success' })
        }
        await Promise.all([this.searchChargers(), this.refreshActiveAssignments(), this.refreshQueue()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    // Sorting is delegated to the API via sessionsOrder, so keep the rows as returned
    passthroughSort (items) {
      return items
    },
    async refreshQueue () {
      this.stopSessionPoll()
      this.isLoading = true
      try {
        this.sessions = await getChargingAssignmentQueue({
          chargePointId: this.filters.chargePointId || undefined,
          idTag: this.filters.idTag || undefined,
          dateFrom: this.filters.dateFrom || undefined,
          dateTo: this.filters.dateTo || undefined,
          order: this.sessionsOrder || undefined,
          limit: 100
        })
        this.syncSelectedSessionFromSessions()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.isLoading = false
        this.scheduleSessionPoll()
      }
    },
    openSessionDialog (session) {
      this.selectedSession = Object.assign({}, session)
      this.assignment.transportationProfile = session && session.transportation_profile_id
        ? {
          id: session.transportation_profile_id,
          licensePlateNumber: session.license_plate_number || 'Assigned vehicle'
        }
        : null
      this.assignment.vehicleClass = session && session.vehicle_class ? session.vehicle_class : 'fleet_bus'
      this.assignment.applyToIdTag = true
      this.assignment.notes = ''
      this.dialogVisible = true
    },
    async onAssignSession () {
      if (!this.selectedSession || !this.selectedSession.ocpp_transaction_id) {
        return
      }
      if (!this.assignment.transportationProfile || !this.assignment.transportationProfile.id) {
        addAlert({ message: 'Please select a vehicle profile first', type: 'error', isModal: true })
        return
      }

      this.actionInProgress = true
      try {
        await assignChargingSession({
          transactionId: this.selectedSession.ocpp_transaction_id,
          transportationProfileId: this.assignment.transportationProfile.id,
          idTag: this.selectedSession.id_tag,
          vehicleClass: this.assignment.vehicleClass,
          applyToIdTag: this.assignment.applyToIdTag,
          notes: this.assignment.notes
        })
        addAlert({ message: 'Charging session assignment updated', type: 'success' })
        await this.refreshQueue()
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async onStartProvisioning () {
      await this.runIdTagAction(startProvisioning, 'Provisioning started')
    },
    async onFinalizeProvisioning () {
      await this.runIdTagAction(finalizeProvisioning, 'Provisioning finalized')
    },
    async onReProvision () {
      await this.runIdTagAction(reProvision, 'Re-provisioning started')
    },
    async onStopSelectedSession () {
      if (!this.canStopSelectedSession) {
        return
      }
      const transactionLabel = this.selectedSession.ocpp_transaction_id ? ' transaction ' + this.selectedSession.ocpp_transaction_id : ''
      const connectorLabel = this.selectedSession.connector_id ? ' gun ' + this.selectedSession.connector_id : ''
      const chargerLabel = this.selectedSession.charge_point_id ? this.selectedSession.charge_point_id + connectorLabel + transactionLabel : 'this active charge'
      if (!confirm('Confirm stopping charging for ' + chargerLabel + '?')) {
        return
      }
      this.actionInProgress = true
      try {
        await stopChargingTransaction({
          chargePointId: this.selectedSession.charge_point_id,
          transactionId: this.selectedSession.ocpp_transaction_id,
          reason: 'Remote'
        })
        addAlert({ message: 'Remote stop request sent', type: 'success' })
        await Promise.all([this.refreshQueue(), this.refreshActiveAssignments(), this.searchChargers()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async onCloseSelectedStaleSession () {
      if (!this.canCloseSelectedStaleSession) {
        return
      }
      const transactionLabel = this.selectedSession.ocpp_transaction_id ? ' transaction ' + this.selectedSession.ocpp_transaction_id : ''
      const connectorLabel = this.selectedSession.connector_id ? ' gun ' + this.selectedSession.connector_id : ''
      const chargerLabel = this.selectedSession.charge_point_id ? this.selectedSession.charge_point_id + connectorLabel + transactionLabel : 'this session'
      const message = 'Confirm closing stale charging session for ' + chargerLabel + '? This will not send a charger command and should only be used when the charger no longer has this transaction active.'
      if (!confirm(message)) {
        return
      }
      this.actionInProgress = true
      try {
        await closeStaleChargingSession({
          transactionId: this.selectedSession.ocpp_transaction_id,
          notes: 'Closed from Fleet Charging manage dialog'
        })
        addAlert({ message: 'Stale charging session closed', type: 'success' })
        await Promise.all([this.refreshQueue(), this.refreshActiveAssignments(), this.searchChargers()])
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    },
    async runIdTagAction (handler, successMessage) {
      if (!this.canRunSelectedSessionIdTagAction) {
        return
      }
      this.actionInProgress = true
      try {
        await handler({ idTag: this.selectedSession.id_tag })
        addAlert({ message: successMessage, type: 'success' })
        await this.refreshQueue()
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.actionInProgress = false
      }
    }
  },
  beforeDestroy () {
    this.stopSessionPoll()
    this.closeMapPopup()
    this.mapMarkerEventsRegistered = false
  }
}
</script>

<style scoped>
.fleet-charging-panel__charger-list {
  max-height: 360px;
  overflow-y: auto;
}

.fleet-charging-panel__progress-cell {
  min-width: 150px;
}
</style>
